import React, { useState } from 'react';
import { Input, Select, Button, Space, Tooltip } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CaretRightOutlined,
  CaretDownOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { createEmptyNode } from './treeFieldUtils';

const VALUE_TYPE_OPTIONS = [
  { label: '字符串', value: 'string' },
  { label: '数组', value: 'array' },
  { label: '对象', value: 'object' },
];

const DEPTH_COLORS = {
  0: '#1677FF',
  1: '#52C41A',
  2: '#FA8C16',
  3: '#EB2F96',
  4: '#722ED1',
  5: '#13C2C2',
};

const DEPTH_LABELS = {
  0: '一级',
  1: '二级',
  2: '三级',
  3: '四级',
  4: '五级',
  5: '六级',
};

const FieldNodeRow = ({
  node,
  onUpdate,
  onDelete,
  onAddSibling,
  maxDepth = 2,
}) => {
  const [expanded, setExpanded] = useState(true);

  const hasChildren = node.children.length > 0;
  const canAddChildren = node.depth < maxDepth && (node.valueType === 'object' || node.valueType === 'array');
  const depthColor = DEPTH_COLORS[node.depth] ?? '#8C8C8C';
  const depthLabel = DEPTH_LABELS[node.depth] ?? '子级';

  const handleKeyChange = (value) => {
    onUpdate({ ...node, key: value });
  };

  const handleValueTypeChange = (valueType) => {
    onUpdate({
      ...node,
      valueType,
      stringValue: valueType === 'string' ? node.stringValue : '',
      arrayValues: valueType === 'array' ? node.arrayValues : [],
      children: valueType === 'object' || valueType === 'array' ? node.children : [],
    });
  };

  const handleStringValueChange = (value) => {
    onUpdate({ ...node, stringValue: value });
  };

  const handleArrayValueAdd = () => {
    onUpdate({ ...node, arrayValues: [...node.arrayValues, ''] });
  };

  const handleArrayValueChange = (index, value) => {
    const newValues = [...node.arrayValues];
    newValues[index] = value;
    onUpdate({ ...node, arrayValues: newValues });
  };

  const handleArrayValueDelete = (index) => {
    const newValues = node.arrayValues.filter((_, idx) => idx !== index);
    onUpdate({ ...node, arrayValues: newValues });
  };

  const handleAddChild = () => {
    const newChild = createEmptyNode(node.depth + 1);
    onUpdate({ ...node, children: [...node.children, newChild] });
  };

  const handleUpdateChild = (childIndex, updatedChild) => {
    const newChildren = [...node.children];
    newChildren[childIndex] = updatedChild;
    onUpdate({ ...node, children: newChildren });
  };

  const handleDeleteChild = (childIndex) => {
    const newChildren = node.children.filter((_, idx) => idx !== childIndex);
    onUpdate({ ...node, children: newChildren });
  };

  const handleAddChildSibling = (afterIndex) => {
    const newChild = createEmptyNode(node.depth + 1);
    const newChildren = [...node.children];
    newChildren.splice(afterIndex + 1, 0, newChild);
    onUpdate({ ...node, children: newChildren });
  };

  const indentWidth = node.depth * 24;

  return (
    <div className="field-node-wrapper">
      <div className="field-node-row" style={{ paddingLeft: indentWidth }}>
        <div className="field-node-expand-btn">
          {hasChildren ? (
            <Button
              type="text"
              size="small"
              icon={expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
              onClick={() => setExpanded(!expanded)}
              style={{ color: depthColor }}
            />
          ) : (
            <span style={{ width: 24, display: 'inline-block' }} />
          )}
        </div>

        <div
          className="field-depth-dot"
          style={{ background: depthColor }}
          title={depthLabel}
        />

        <Input
          placeholder="字段名"
          value={node.key}
          onChange={(e) => handleKeyChange(e.target.value)}
          style={{ width: 140, flexShrink: 0 }}
          size="small"
        />

        <Select
          value={node.valueType}
          onChange={handleValueTypeChange}
          options={VALUE_TYPE_OPTIONS}
          style={{ width: 96, flexShrink: 0 }}
          size="small"
          className={`field-type-select type-${node.valueType}`}
          popupClassName="field-type-select-popup"
        />

        {node.valueType === 'string' && (
          <Input
            placeholder="字段值"
            value={node.stringValue}
            onChange={(e) => handleStringValueChange(e.target.value)}
            style={{ flex: 1, minWidth: 80 }}
            size="small"
          />
        )}

        {node.valueType === 'array' && node.children.length === 0 && (
          <div className="array-values-inline">
            {node.arrayValues.map((val, idx) => (
              <div key={idx} className="array-value-item">
                <Input
                  placeholder={`值${idx + 1}`}
                  value={val}
                  onChange={(e) => handleArrayValueChange(idx, e.target.value)}
                  size="small"
                  style={{ width: 140 }}
                />
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleArrayValueDelete(idx)}
                />
              </div>
            ))}
            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={handleArrayValueAdd}
              style={{ fontSize: 12, flexShrink: 0 }}
            >
              添加值
            </Button>
          </div>
        )}

        {(node.valueType === 'object' || (node.valueType === 'array' && node.children.length > 0)) && (
          <span className="field-node-type-hint">
            {node.valueType === 'object' ? '对象' : '对象数组'}（展开查看子字段）
          </span>
        )}

        <div className="field-node-actions">
          {canAddChildren && (
            <Tooltip title="添加子字段">
              <Button
                type="text"
                size="small"
                icon={<PlusCircleOutlined />}
                onClick={handleAddChild}
                style={{ color: '#52C41A' }}
              />
            </Tooltip>
          )}
          <Tooltip title="在下方添加同级字段">
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              onClick={onAddSibling}
              style={{ color: '#1677FF' }}
            />
          </Tooltip>
          <Tooltip title="删除此字段">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={onDelete}
            />
          </Tooltip>
        </div>
      </div>

      {expanded && hasChildren && (
        <div className="field-node-children">
          {node.children.map((child, childIndex) => (
            <FieldNodeRow
              key={child.id}
              node={child}
              onUpdate={(updated) => handleUpdateChild(childIndex, updated)}
              onDelete={() => handleDeleteChild(childIndex)}
              onAddSibling={() => handleAddChildSibling(childIndex)}
              maxDepth={maxDepth}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export { FieldNodeRow };
